import deepmerge from "deepmerge";

import { ContainerKind, type MyLensesRole, MyLensesRoleType } from "../../../gql-generated/types";
import {
    LENS_FOLDER_ROLE_TYPE_PERMISSIONS,
    type LensFolderRoleType,
    type LensFolderRoleTypePermissions,
    ORGANIZATION_ROLE_TYPE_PERMISSIONS,
    type OrganizationRoleType,
} from "../../constants";

const getUserContainerPermissionsForAllRoles = (
    containerKey: string,
    rolesStore: Record<string, MyLensesRole[]>,
): LensFolderRoleTypePermissions[] => {
    const containerRoles = rolesStore[containerKey] ?? [];
    const userContainerPermissions: LensFolderRoleTypePermissions[] = [];
    for (const role of containerRoles) {
        const { containerKind, roleType } = role;
        if (containerKind === ContainerKind.ContainerKindOrganization) {
            userContainerPermissions.push(ORGANIZATION_ROLE_TYPE_PERMISSIONS[roleType as OrganizationRoleType]);
        } else if (containerKind === ContainerKind.ContainerKindLensFolder) {
            userContainerPermissions.push(LENS_FOLDER_ROLE_TYPE_PERMISSIONS[roleType as LensFolderRoleType]);
        }
    }
    return userContainerPermissions;
};

export const mergeUserContainerPermissions = (
    userContainerPermissions: LensFolderRoleTypePermissions[],
): LensFolderRoleTypePermissions => {
    return userContainerPermissions.reduce(
        (allPermissions, permissions) => deepmerge(allPermissions, permissions),
        LENS_FOLDER_ROLE_TYPE_PERMISSIONS[MyLensesRoleType.RoleTypeUnset],
    );
};

const getUserContainerPermissions = (
    containerKey: string,
    rolesStore: Record<string, MyLensesRole[]>,
): LensFolderRoleTypePermissions => {
    const userPermissionsForAllRoles = getUserContainerPermissionsForAllRoles(containerKey, rolesStore);

    return mergeUserContainerPermissions(userPermissionsForAllRoles);
};

export const getLensFolderPermissions = (
    organizationId: string,
    lensFolderId: string,
    rolesStore: Record<string, MyLensesRole[]> | null,
): LensFolderRoleTypePermissions => {
    if (!rolesStore || !lensFolderId || !organizationId) {
        return LENS_FOLDER_ROLE_TYPE_PERMISSIONS[MyLensesRoleType.RoleTypeUnset];
    }
    const lensFolderKey = `${ContainerKind.ContainerKindLensFolder}:${lensFolderId}`;
    if (lensFolderKey in rolesStore) {
        return getUserContainerPermissions(lensFolderKey, rolesStore);
    }
    const organizationKey = `${ContainerKind.ContainerKindOrganization}:${organizationId}`;
    return getUserContainerPermissions(organizationKey, rolesStore);
};
